import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

/** px deadzone near each edge within which that direction is treated as fully scrolled. */
const EDGE_THRESHOLD_PX = 2;
/** Fraction of the visible width one arrow press advances — a "page" of columns. */
const SCROLL_STEP_RATIO = 0.8;

export interface PunchupHorizontalScroll {
	/** True when the element is scrolled away from its left edge (left arrow active). */
	canScrollLeft: boolean;
	/** True when content remains hidden to the right (right arrow active). */
	canScrollRight: boolean;
	/** Smooth-scroll one page of columns toward the start. No-op at the left edge. */
	scrollLeft: () => void;
	/** Smooth-scroll one page of columns toward the end. No-op at the right edge. */
	scrollRight: () => void;
}

/**
 * Tracks the horizontal-overflow state of a scroll container and exposes paged
 * left/right scrolling for an arrow control.
 *
 * Green clone of the red-zone `useHorizontalScrollIndicators`, extended with a
 * `ResizeObserver` so the arrows re-evaluate when the table's own size changes
 * (rows load in, a column is toggled, the container resizes) — `window`'s
 * `resize` event alone misses those. Used by `PunchupTableContainer` to feed the
 * overflow chevrons rendered in `PunchupActionsHeaderCell`.
 */
export const usePunchupHorizontalScroll = (ref: RefObject<HTMLElement>): PunchupHorizontalScroll => {
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const recompute = () => {
			const { scrollLeft, scrollWidth, clientWidth } = element;
			setCanScrollLeft(scrollLeft > EDGE_THRESHOLD_PX);
			setCanScrollRight(scrollLeft < scrollWidth - clientWidth - EDGE_THRESHOLD_PX);
		};

		recompute();
		element.addEventListener('scroll', recompute);
		window.addEventListener('resize', recompute);
		// Re-evaluate on the table's own size changes (data loads, column toggled, container resized),
		// which the window 'resize' event does not cover. Guarded for jsdom/older runtimes.
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null;
		observer?.observe(element);

		return () => {
			element.removeEventListener('scroll', recompute);
			window.removeEventListener('resize', recompute);
			observer?.disconnect();
		};
	}, [ref]);

	const scrollByPage = useCallback(
		(direction: 'left' | 'right') => {
			const element = ref.current;
			if (!element) return;
			const amount = element.clientWidth * SCROLL_STEP_RATIO;
			element.scrollTo({
				left: direction === 'left' ? element.scrollLeft - amount : element.scrollLeft + amount,
				behavior: 'smooth',
			});
		},
		[ref]
	);

	const scrollLeft = useCallback(() => scrollByPage('left'), [scrollByPage]);
	const scrollRight = useCallback(() => scrollByPage('right'), [scrollByPage]);

	// Memoise so context consumers only re-render when an edge boolean actually flips
	// (the callbacks are already stable) — not on every scroll event.
	return useMemo(
		() => ({ canScrollLeft, canScrollRight, scrollLeft, scrollRight }),
		[canScrollLeft, canScrollRight, scrollLeft, scrollRight]
	);
};
